import { AlertCircle, FolderOpen, ListTodo, Loader2 } from "lucide-react";
import { AppShell } from "../../layouts/AppShell";
import { useDashboardSummary } from "../../../api/opsV2/hooks";

type OpsDashboardPageProps = {
  userName?: string;
  onBack?: () => void;
  onOpenEngagements?: () => void;
  onOpenMyWork?: () => void;
};

export function OpsDashboardPage({
  userName = "Utilisateur",
  onBack,
  onOpenEngagements,
  onOpenMyWork,
}: OpsDashboardPageProps) {
  const { data: summary, loading, error, refetch } = useDashboardSummary();

  return (
    <AppShell
      userName={userName}
      topbar={
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="text-sm text-muted hover:text-text"
            >
              Opérations
            </button>
          )}
          <span className="text-sm font-medium text-text">Cockpit</span>
        </div>
      }
    >
      <div className="ds-container py-8">
        <h1 className="text-xl font-semibold text-text mb-2">Cockpit Directeur</h1>
        <p className="text-sm text-muted mb-6">
          Vue d’ensemble des risques SLA, blocages et charge. Liens directs vers les dossiers et activités.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted mb-6">
            <Loader2 className="h-4 w-4 animate-spin" />
            Chargement des indicateurs…
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive mb-6">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error.message}</span>
            <button type="button" onClick={() => refetch()} className="text-primary underline">
              Réessayer
            </button>
          </div>
        )}
        {!loading && !error && summary && (
          <div className="grid gap-3 sm:grid-cols-3 mb-6 max-w-2xl">
            <div className="rounded-lg border border-border bg-surface p-3">
              <div className="text-2xl font-semibold text-text tabular-nums">
                {summary.overdue_activities_count}
              </div>
              <div className="text-xs text-muted">Activités en retard</div>
              {onOpenMyWork && summary.overdue_activities_count > 0 && (
                <button
                  type="button"
                  onClick={onOpenMyWork}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Voir Mes activités →
                </button>
              )}
            </div>
            <div className="rounded-lg border border-border bg-surface p-3">
              <div className="text-2xl font-semibold text-text tabular-nums">
                {summary.blocked_activities_count}
              </div>
              <div className="text-xs text-muted">Activités bloquées</div>
              {onOpenMyWork && summary.blocked_activities_count > 0 && (
                <button
                  type="button"
                  onClick={onOpenMyWork}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Voir Mes activités →
                </button>
              )}
            </div>
            <div className="rounded-lg border border-border bg-surface p-3">
              <div className="text-2xl font-semibold text-text tabular-nums">
                {summary.blocked_process_cases_count}
              </div>
              <div className="text-xs text-muted">Dossiers bloqués</div>
              {onOpenEngagements && summary.blocked_process_cases_count > 0 && (
                <button
                  type="button"
                  onClick={onOpenEngagements}
                  className="mt-2 text-xs text-primary hover:underline"
                >
                  Voir les dossiers →
                </button>
              )}
            </div>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          {onOpenEngagements && (
            <button
              type="button"
              onClick={onOpenEngagements}
              className="flex items-center gap-3 rounded-lg border border-border bg-surface p-4 text-left hover:border-primary/30 transition"
            >
              <FolderOpen className="h-8 w-8 text-primary" />
              <div>
                <div className="font-medium text-text">Ouvrir les dossiers</div>
                <div className="text-xs text-muted">Liste des engagements</div>
              </div>
            </button>
          )}
          {onOpenMyWork && (
            <button
              type="button"
              onClick={onOpenMyWork}
              className="flex items-center gap-3 rounded-lg border border-border bg-surface p-4 text-left hover:border-primary/30 transition"
            >
              <ListTodo className="h-8 w-8 text-primary" />
              <div>
                <div className="font-medium text-text">Mes activités</div>
                <div className="text-xs text-muted">Tâches assignées</div>
              </div>
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
